import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Camera, Upload, X } from 'lucide-react';
import { api, useResource } from '../api.js';
import { useAccount } from '../Account.js';
import { IMAGE_MAX_BYTES, IMAGE_RIGHTS_STATEMENT } from '../../../../packages/shared/src/image-submissions.js';

export type ImageSubmission = {
  id: string; status: 'PENDING' | 'APPROVED' | 'REMOVED'; createdAt: string; reviewedAt: string | null;
  rightsStatement?: string; rightsConfirmedAt?: string;
  fragrance: { id: string; name: string; slug: string; concentration: string; brand?: { name: string } | null };
  media: { id: string; alt: string; width: number; height: number } | null;
  user?: { id: string; name: string; email: string; createdAt: string };
};
export const submissionStatus = { PENDING: 'Awaiting review', APPROVED: 'Approved', REMOVED: 'Removed' };
export function ContributePhotoButton({ fragrance, onClick }: { fragrance: { name: string; media: unknown }; onClick: () => void }) {
  const { user } = useAccount();
  if (!user || fragrance.media) return null;
  return <button type="button" className="contribute-photo-button" onClick={onClick} aria-label={'Contribute a photo of ' + fragrance.name}><Camera size={16} aria-hidden />Contribute a photo</button>;
}
export function ImageSubmissionForm({ fragrance, open, onClose }: { fragrance: { id: string; name: string; media: unknown }; open: boolean; onClose: () => void }) {
  const { user } = useAccount();
  const id = useId();
  const dialog = useRef<HTMLDialogElement>(null), heading = useRef<HTMLHeadingElement>(null);
  const [file, setFile] = useState<File | null>(null), [preview, setPreview] = useState('');
  const [confirmed, setConfirmed] = useState(false), [busy, setBusy] = useState(false), [error, setError] = useState(''), [sent, setSent] = useState(false), [dragging, setDragging] = useState(false);
  useEffect(() => {
    if (!file) { setPreview(''); return; }
    const url = URL.createObjectURL(file); setPreview(url); return () => URL.revokeObjectURL(url);
  }, [file]);
  // The native modal dialog traps focus, closes on Escape and returns focus to the trigger.
  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (open && !element.open) { element.showModal(); heading.current?.focus(); }
    else if (!open && element.open) element.close();
  }, [open]);
  useEffect(() => { if (sent) heading.current?.focus(); }, [sent]);
  if (fragrance.media || !user) return null;
  function choose(next: File | null | undefined) {
    setError(''); setConfirmed(false);
    if (!next) { setFile(null); return; }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(next.type)) { setError('Choose a JPEG, PNG or WebP picture.'); return; }
    if (next.size > IMAGE_MAX_BYTES) { setError('Choose a picture smaller than 8 MB.'); return; }
    setFile(next);
  }
  function close() { if (!busy) dialog.current?.close(); }
  async function submit(event: FormEvent) {
    event.preventDefault(); setError('');
    if (!file || !confirmed) { setError('Choose a picture and confirm your rights to use it.'); return; }
    setBusy(true);
    try {
      const session = await api<{ csrfToken: string }>('/api/v1/account/session');
      const body = new FormData(); body.set('image', file); body.set('fragranceId', fragrance.id); body.set('rightsConfirmed', 'true');
      await api('/api/v1/image-submissions', { method: 'POST', headers: { 'X-CSRF-Token': session.csrfToken }, body });
      setSent(true); setFile(null); setConfirmed(false);
    } catch (failure) { setError((failure as Error).message); } finally { setBusy(false); }
  }
  return <>
    {sent && !open && <div className="submission-success" role="status"><strong>Photo sent for review</strong><p>Your photo will appear here if an editor approves it. <Link to="/profile">View your submissions</Link>.</p></div>}
    <dialog ref={dialog} className="modal-dialog" aria-labelledby={id + '-title'} aria-describedby={id + '-intro'}
      onClose={onClose} onCancel={event => { if (busy) event.preventDefault(); }}
      onClick={event => { if (event.target === dialog.current) close(); }}>
      <div className="modal-dialog-panel">
        <header className="modal-dialog-header">
          <h2 id={id + '-title'} ref={heading} tabIndex={-1}>{sent ? 'Photo sent for review' : 'Add a photo of ' + fragrance.name}</h2>
          <button type="button" className="modal-close" onClick={close} disabled={busy} aria-label="Close"><X size={18} aria-hidden /></button>
        </header>
        {sent ? <div className="modal-dialog-sent">
          <p id={id + '-intro'}>Thank you. An editor will check your photo, and it will appear on this page once approved. You can follow its progress on your profile.</p>
          <div className="modal-dialog-actions"><Link className="text-link" to="/profile">View your submissions</Link><button type="button" className="button" onClick={close}>Done</button></div>
        </div> : <form onSubmit={submit} noValidate>
          <p id={id + '-intro'} className="modal-dialog-intro">Have a picture you own? Send it for editorial review. It won’t appear publicly until approved.</p>
          <label className={'submission-drop' + (dragging ? ' dragging' : '') + (preview ? ' has-preview' : '')}
            onDragOver={event => { event.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}
            onDrop={event => { event.preventDefault(); setDragging(false); choose(event.dataTransfer.files[0]); }}>
            <input className="sr-only" type="file" name="image" accept="image/jpeg,image/png,image/webp" disabled={busy} aria-describedby={id + '-file-help'} onChange={event => choose(event.target.files?.[0])} />
            {preview ? <>
              <img src={preview} alt="Your selected photo, not yet submitted" />
              <span className="submission-drop-caption"><strong>{file?.name}</strong><span>Choose a different photo</span></span>
            </> : <>
              <span className="submission-drop-icon" aria-hidden="true"><Upload size={20} /></span>
              <strong>Choose a photo</strong>
              <span>or drag it here</span>
            </>}
          </label>
          <p id={id + '-file-help'} className="submission-help">JPEG, PNG or WebP · up to 8 MB. Use a clear photo of the correct bottle and concentration. Avoid people or personal information.</p>
          <label className="submission-rights"><input type="checkbox" checked={confirmed} onChange={event => setConfirmed(event.target.checked)} disabled={busy || !file} /><span>{IMAGE_RIGHTS_STATEMENT}</span></label>
          <p className="submission-help">Editors can see your account name and email to review your submission. <Link to="/privacy#image-submissions">How we handle photos</Link>.</p>
          {error && <p className="error-message" role="alert">{error}</p>}
          <div className="modal-dialog-actions">
            <button type="button" className="modal-cancel" onClick={close} disabled={busy}>Cancel</button>
            <button className="button" disabled={busy || !file || !confirmed}><Upload size={16} aria-hidden />{busy ? 'Uploading…' : 'Submit for review'}</button>
          </div>
        </form>}
      </div>
    </dialog>
  </>;
}
export function MyImageSubmissions() {
  const { data, loading, error, reload } = useResource<{ items: ImageSubmission[] }>('/api/v1/image-submissions');
  return <section className="my-submissions"><h2>Your photo submissions</h2><p>Follow the progress of your recent fragrance photos.</p>
    {loading && <p role="status">Loading submissions…</p>}{error && <p role="alert">{error} <button type="button" onClick={reload}>Try again</button></p>}
    {data && (data.items.length ? <ul>{data.items.map(item => <li key={item.id}>
      {item.media && <img src={'/media/' + item.media.id + '?size=thumb'} alt={item.media.alt} loading="lazy" />}
      <div><Link to={'/fragrances/' + item.fragrance.slug}>{item.fragrance.name}</Link><p>{item.fragrance.concentration}</p><span className="status-badge">{submissionStatus[item.status]}</span></div>
    </li>)}</ul> : <p className="submission-help">No photos submitted yet. Look for “Contribute a photo” on a fragrance page with a missing image.</p>)}
  </section>;
}
